// The admin console's new-event marker editor: the default style, the per-kind
// overrides, and a live preview of each.
//
// The preview is not decoration. Every knob here is a duration, a distance or a
// colour whose effect nobody can picture from a number — "ring speed 1500 ms"
// and "pulse intensity 22%" mean nothing until you watch them — and the
// alternative to a preview is saving, reloading the cockpit and waiting for a
// real event to arrive. It draws with eventstyle.ts's own ringProgress /
// pulseFactor / stageAt, the same functions the map's sprites call, so the two
// cannot disagree about the SHAPE of the animation. What it does not reproduce
// is MapLibre's zoom ramp and label collision; it is a marker at z12 on a plain
// background, which is the thing being tuned.

import { ageColor, ageOpacity, liveFreshMs } from './when';
import type { EventMarkerConfig, EventMarkerStyle, MarkerStage } from './eventstyle';
import {
  DEFAULT_MARKER_STYLE, GROUP_LABELS, MARKER_FIELDS, MAX_STAGES, glyphOf, newMs,
  normalizeGlyph, pulseFactor, pulseMs, ringMs, ringProgress, stageAt,
} from './eventstyle';
import { LIVE_KINDS, SHIPPED_GLYPHS } from './livekinds';

const esc = (v: unknown): string => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** The ages the preview offers. A style is judged at more than one moment —
 *  the arrival, the minute after, and what it has settled into an hour later —
 *  and the settle/stage knobs are invisible without the later ones. */
const PREVIEW_AGES: [string, number][] = [
  ['0s', 0], ['30s', 30_000], ['2m', 120_000], ['10m', 600_000],
  ['1h', 3_600_000], ['6h', 21_600_000],
];

interface Live {
  cfg: EventMarkerConfig;
  /** Which kind each editor previews — 'default' previews `impact`. */
  previews: { host: HTMLCanvasElement; style: () => EventMarkerStyle;
              kind: string; ageMs: number }[];
  raf: number;
}

// --- preview drawing ----------------------------------------------------------

function markerColor(style: EventMarkerStyle, ageMs: number): [string, number] {
  const stage = stageAt(style, ageMs);
  if (stage) return [stage.color, stage.opacity];
  const now = Date.now();
  return [ageColor(now - ageMs, now), ageOpacity(now - ageMs, now)];
}

function rgba(hex: string, alpha: number): string {
  const v = parseInt(hex.replace('#', ''), 16);
  if (!Number.isFinite(v)) return `rgba(255,23,68,${alpha})`;
  // eslint-disable-next-line no-bitwise
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`;
}

function drawPreview(canvas: HTMLCanvasElement, style: EventMarkerStyle,
                     glyphChar: string, ageMs: number, tMs: number): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const css = canvas.clientWidth || 200;
  if (canvas.width !== Math.round(css * dpr)) {
    canvas.width = Math.round(css * dpr);
    canvas.height = Math.round(css * dpr);
  }
  const ctx = canvas.getContext('2d')!;
  const size = canvas.width / dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size, size);

  // A neutral backdrop with a hairline grid: a marker judged against a flat
  // panel colour looks nothing like one judged against a map.
  ctx.fillStyle = '#7c8b93';
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(255,255,255,.14)';
  ctx.lineWidth = 1;
  for (let g = 20; g < size; g += 20) {
    ctx.beginPath();
    ctx.moveTo(g, 0); ctx.lineTo(g, size);
    ctx.moveTo(0, g); ctx.lineTo(size, g);
    ctx.stroke();
  }

  const fresh = liveFreshMs();
  const [color, stageOpacity] = markerColor(style, ageMs);
  const flash = style.ring && ageMs < ringMs(style, fresh);
  const pulsing = style.pulse && style.pulsePct > 0 && ageMs < pulseMs(style, fresh);
  const isNew = ageMs < newMs(style, fresh);
  const scale = isNew ? (pulsing ? 1 + style.pulsePct / 200 : 1) : style.settleScale;
  const opacity = stageOpacity;
  const cx = size / 2;
  const cy = size / 2;

  if (style.halo && isNew) {
    ctx.fillStyle = rgba(style.haloColor || color, style.haloOpacity * opacity);
    ctx.beginPath();
    ctx.arc(cx, cy, style.haloPx, 0, Math.PI * 2);
    ctx.fill();
  }
  if (flash) {
    for (let i = 0; i < style.ringCount; i += 1) {
      const p = ringProgress(style, i, tMs);
      ctx.beginPath();
      ctx.arc(cx, cy, 3 + (style.ringMaxPx - 3) * p, 0, Math.PI * 2);
      ctx.strokeStyle = rgba(style.ringColor || color,
                             style.ringOpacity * (1 - p) * opacity);
      ctx.lineWidth = Math.max(0.6, style.ringWidthPx * (1 - p));
      ctx.stroke();
    }
  }
  if (style.dot) {
    ctx.globalAlpha = opacity;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(0.5, style.dotPx * scale), 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.9)';
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
  if (style.symbol) {
    const beat = pulsing ? pulseFactor(style, tMs) : 1;
    // 15 px is the glyph's apparent size at icon-size 1 on the map (a 30 px
    // draw on a pixelRatio-2 sprite); the offsets mirror the layer's own.
    const px = 15 * style.symbolScale * (pulsing ? beat : scale);
    const y = style.dot ? cy - 16 : cy;
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.font = `${px * 2}px "Segoe UI Emoji", "Apple Color Emoji", `
      + '"Noto Color Emoji", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (style.symbolTint) {
      const scratch = document.createElement('canvas');
      scratch.width = Math.ceil(px * 3);
      scratch.height = Math.ceil(px * 3);
      const sctx = scratch.getContext('2d')!;
      sctx.font = ctx.font;
      sctx.textAlign = 'center';
      sctx.textBaseline = 'middle';
      sctx.fillText(glyphChar, scratch.width / 2, scratch.height / 2);
      sctx.globalCompositeOperation = 'source-in';
      sctx.fillStyle = color;
      sctx.fillRect(0, 0, scratch.width, scratch.height);
      ctx.shadowColor = 'rgba(0,0,0,.5)';
      ctx.shadowBlur = 3;
      ctx.drawImage(scratch, cx - scratch.width / 2, y - scratch.height / 2);
    } else {
      ctx.shadowColor = 'rgba(0,0,0,.45)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetY = 1;
      ctx.fillText(glyphChar, cx, y);
    }
    ctx.restore();
  }

  // What phase this actually is, in words — a preview that shows only pixels
  // leaves "why did the ring stop" unanswerable.
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'rgba(0,0,0,.55)';
  const phase = [flash ? 'ring' : '', pulsing ? 'pulse' : '',
                 isNew ? '' : 'settled'].filter(Boolean).join(' + ') || 'quiet';
  ctx.fillText(phase, 6, 6);
}

// --- field rendering ----------------------------------------------------------

function fieldRow(style: EventMarkerStyle, key: string): string {
  const f = MARKER_FIELDS.find((item) => item.key === key)!;
  const value = style[f.key];
  const hint = f.hint ? `<p class="adm-hint mk-hint">${f.hint}</p>` : '';
  const shown = !f.needs || style[f.needs] ? '' : ' hidden';
  if (f.kind === 'bool') {
    return `<div class="mk-row" data-row="${f.key}"${shown}>
      <label class="adm-check"><input type="checkbox" data-f="${f.key}"
        ${value ? 'checked' : ''} /> ${esc(f.label)}</label>${hint}</div>`;
  }
  if (f.kind === 'color') {
    const auto = value === '';
    return `<div class="mk-row" data-row="${f.key}"${shown}>
      <label class="mk-label">${esc(f.label)}</label>
      <div class="mk-colorwrap">
        <input type="color" data-f="${f.key}"
          value="${auto ? '#ff1744' : esc(value)}" ${auto ? 'disabled' : ''} />
        <label class="adm-check"><input type="checkbox" data-auto="${f.key}"
          ${auto ? 'checked' : ''} /> ${esc(f.auto ?? 'Auto')}</label>
      </div>${hint}</div>`;
  }
  return `<div class="mk-row" data-row="${f.key}"${shown}>
    <label class="mk-label">${esc(f.label)}</label>
    <div class="mk-numwrap">
      <input type="range" data-range="${f.key}" min="${f.min}" max="${f.max}"
        step="${f.step}" value="${esc(value)}" />
      <input type="number" data-f="${f.key}" min="${f.min}" max="${f.max}"
        step="${f.step}" value="${esc(value)}" />
    </div>${hint}</div>`;
}

function stagesEditor(style: EventMarkerStyle): string {
  const rows = style.stages.map((s, i) => `
    <div class="mk-stage" data-stage="${i}">
      <input type="number" data-sf="atSeconds" min="0" max="604800" step="1"
        value="${s.atSeconds}" title="Age in seconds when this colour takes over"
        ${i === 0 ? 'disabled' : ''} />
      <input type="color" data-sf="color" value="${esc(s.color)}" />
      <input type="number" data-sf="opacity" min="0" max="1" step="0.05"
        value="${s.opacity}" title="Opacity from this point on" />
      <button class="mini" data-stage-del="${i}" title="Remove this stop">✕</button>
    </div>`).join('');
  return `<fieldset class="mk-group mk-group-wide"><legend>Colour over time</legend>
    <p class="adm-hint">Empty = the global age ramp (red → orange → yellow →
      grey over the colour-change time above), which is what every kind starts
      on. Add stops to give this one its own — each stop names an age in
      seconds, and the colour and opacity the marker takes from then on. The
      first stop always starts at 0.</p>
    ${style.stages.length
      ? '<p class="adm-hint mk-stage-key">age (s) · colour · opacity</p>' : ''}
    <div class="mk-stages">${rows
      || '<p class="adm-hint">Using the global age ramp.</p>'}</div>
    <button class="mini" data-stage-add ${style.stages.length >= MAX_STAGES
      ? 'disabled' : ''}>+ Add colour stop</button>
  </fieldset>`;
}

function editorHtml(style: EventMarkerStyle, id: string, kind: string,
                    title: string, removable: boolean): string {
  const groups = (['ring', 'body', 'pulse'] as const).map((g) => `
    <fieldset class="mk-group"><legend>${esc(GROUP_LABELS[g])}</legend>
      ${MARKER_FIELDS.filter((f) => f.group === g)
        .map((f) => fieldRow(style, f.key)).join('')}
    </fieldset>`).join('');
  return `<div class="adm-card mk-editor" data-style="${esc(id)}" data-kind="${esc(kind)}">
    <header class="mk-head"><h4>${esc(title)}</h4>${removable
      ? `<button class="mini danger" data-drop="${esc(id)}">Remove override</button>` : ''}
    </header>
    <div class="mk-body">
      <div class="mk-fields">${groups}${stagesEditor(style)}</div>
      <div class="mk-preview">
        <canvas width="200" height="200"></canvas>
        <div class="mk-ages">${PREVIEW_AGES.map(([label, ms], i) =>
          `<button class="mini${i === 0 ? ' on' : ''}" data-age="${ms}">${label}</button>`)
          .join('')}</div>
        <p class="adm-hint">Age since the last report. The ring and pulse run
          off that clock; the colour runs off when the event happened.</p>
      </div>
    </div>
  </div>`;
}

/** One input per kind, in vocabulary order. The value shown is what RENDERS
 *  (override or shipped), so the grid doubles as the legend; a kind with an
 *  override is marked and carries a reset. */
function glyphGridHtml(cfg: EventMarkerConfig): string {
  return `<div class="adm-card mk-glyphs">
    <h4>Kind symbols</h4>
    <p class="adm-hint">The symbol each event kind wears — on the map, in the
      feed, the ticker and every kind filter. An emoji, or a character or two;
      blank it to return to the shipped one.</p>
    <div class="mk-glyph-grid">${Object.entries(LIVE_KINDS).map(([k, v]) => {
      const edited = k in cfg.glyphs;
      return `<label class="mk-glyph">
        <input type="text" maxlength="24" data-glyph="${esc(k)}"
               class="${edited ? 'edited' : ''}" value="${esc(glyphOf(k, cfg))}"
               aria-label="Symbol for ${esc(v.label)}" />
        <span class="mk-glyph-label">${esc(v.label)}</span>
        <button type="button" class="mini" data-glyph-reset="${esc(k)}"
                title="Back to the shipped ${esc(SHIPPED_GLYPHS[k] ?? '')}"
                ${edited ? '' : 'hidden'}>↺</button>
      </label>`;
    }).join('')}</div>
  </div>`;
}

// --- mounting -----------------------------------------------------------------

export function markerEditorHtml(cfg: EventMarkerConfig): string {
  const overrides = Object.keys(cfg.kinds).sort();
  const free = Object.keys(LIVE_KINDS).filter((k) => !(k in cfg.kinds)).sort();
  return `
    <h3>Event markers</h3>
    <p class="adm-hint">The symbol each kind wears, and how a live event
      announces itself when it lands on the map. The default applies to every
      kind; an override replaces it wholesale for one kind, so what you see in
      an override card is exactly what renders. Times run from the LAST REPORT
      — a corroborated old event announces itself again, which is the point.
      The flashing-time default is in Map defaults.</p>
    <div class="mk-wrap">
      ${glyphGridHtml(cfg)}
      ${editorHtml(cfg.default, 'default', 'impact', 'Default — every event kind', false)}
      ${overrides.map((k) => editorHtml(cfg.kinds[k]!, k, k,
        `${(LIVE_KINDS[k] ?? { label: k }).label} (${k})`, true)).join('')}
      <div class="adm-card mk-add">
        <label>Customise a kind
          <select id="mk-newkind">${free.map((k) =>
            `<option value="${esc(k)}">${esc((LIVE_KINDS[k] ?? { label: k }).label)}</option>`)
            .join('') || '<option value="">every kind is customised</option>'}</select>
        </label>
        <button class="mini" id="mk-addkind" ${free.length ? '' : 'disabled'}>+ Customise</button>
        <p class="adm-hint">Starts as a copy of the default. Removing an
          override returns that kind to the default.</p>
      </div>
      <footer class="mk-foot">
        <button class="mini commit" id="mk-save">Save marker styles</button>
        <button class="mini" id="mk-reset">Reset to shipped defaults</button>
        <span class="adm-hint">Open tabs pick this up on their next reload.</span>
      </footer>
    </div>`;
}

/** Wire an already-rendered editor. `rerender` re-runs the caller's own render
 *  pass — adding or removing a kind changes the card list, and rebuilding the
 *  whole block is both simpler and safer than splicing DOM around live
 *  animation loops. */
export function wireMarkerEditor(
  host: HTMLElement,
  cfg: EventMarkerConfig,
  onSave: (next: EventMarkerConfig) => Promise<void>,
  rerender: (next: EventMarkerConfig) => void,
): () => void {
  const live: Live = { cfg, previews: [], raf: 0 };

  const styleOf = (id: string): EventMarkerStyle =>
    (id === 'default' ? live.cfg.default : live.cfg.kinds[id]!);

  host.querySelectorAll<HTMLElement>('.mk-editor').forEach((card) => {
    const id = card.dataset.style!;
    const canvas = card.querySelector('canvas') as HTMLCanvasElement;
    const entry = { host: canvas, style: () => styleOf(id), kind: card.dataset.kind!, ageMs: 0 };
    live.previews.push(entry);

    card.querySelectorAll<HTMLButtonElement>('[data-age]').forEach((btn) => {
      btn.addEventListener('click', () => {
        entry.ageMs = Number(btn.dataset.age);
        card.querySelectorAll('[data-age]').forEach((b) => b.classList.remove('on'));
        btn.classList.add('on');
      });
    });

    // Rows whose parent toggle is off stay in the DOM but hidden, so turning
    // the toggle back on restores the numbers the operator had set rather than
    // resetting them — an editor that forgets is worse than a busy one.
    const syncVisible = () => {
      const style = styleOf(id);
      card.querySelectorAll<HTMLElement>('.mk-row[data-row]').forEach((row) => {
        const f = MARKER_FIELDS.find((item) => item.key === row.dataset.row);
        if (f?.needs) row.hidden = !style[f.needs];
      });
    };

    const write = (key: string, value: unknown) => {
      (styleOf(id) as unknown as Record<string, unknown>)[key] = value;
      syncVisible();
    };

    card.addEventListener('input', (ev) => {
      const el = ev.target as HTMLInputElement;
      if (el.dataset.f) {
        const f = MARKER_FIELDS.find((item) => item.key === el.dataset.f)!;
        if (f.kind === 'bool') write(f.key, el.checked);
        else if (f.kind === 'color') write(f.key, el.value);
        else {
          write(f.key, Number(el.value));
          const range = card.querySelector<HTMLInputElement>(`[data-range="${f.key}"]`);
          if (range) range.value = el.value;
        }
      } else if (el.dataset.range) {
        const num = card.querySelector<HTMLInputElement>(`[data-f="${el.dataset.range}"]`);
        if (num) num.value = el.value;
        write(el.dataset.range, Number(el.value));
      } else if (el.dataset.auto) {
        const key = el.dataset.auto;
        const picker = card.querySelector<HTMLInputElement>(`input[type="color"][data-f="${key}"]`);
        if (picker) picker.disabled = el.checked;
        write(key, el.checked ? '' : (picker?.value ?? '#ff1744'));
      } else if (el.dataset.sf) {
        const row = el.closest('.mk-stage') as HTMLElement | null;
        if (!row) return;
        const stage = styleOf(id).stages[Number(row.dataset.stage)];
        if (!stage) return;
        const key = el.dataset.sf as keyof MarkerStage;
        (stage as unknown as Record<string, unknown>)[key] =
          key === 'color' ? el.value : Number(el.value);
      }
    });

    card.addEventListener('click', (ev) => {
      const el = (ev.target as HTMLElement).closest('button');
      if (!el) return;
      const style = styleOf(id);
      if (el.hasAttribute('data-stage-add')) {
        const last = style.stages[style.stages.length - 1];
        style.stages.push({
          atSeconds: style.stages.length ? (last!.atSeconds || 60) * 4 : 0,
          color: last?.color ?? '#ff1744',
          opacity: 1,
        });
        rerender(live.cfg);
      } else if (el.hasAttribute('data-stage-del')) {
        style.stages.splice(Number(el.getAttribute('data-stage-del')), 1);
        if (style.stages.length) style.stages[0]!.atSeconds = 0;
        rerender(live.cfg);
      } else if (el.hasAttribute('data-drop')) {
        delete live.cfg.kinds[el.getAttribute('data-drop')!];
        rerender(live.cfg);
      }
    });
  });

  // Kind symbols. The previews read `glyphOf(kind, live.cfg)` every frame, so
  // an edit shows on the default and override cards without a rerender.
  const glyphs = host.querySelector<HTMLElement>('.mk-glyphs');
  glyphs?.addEventListener('input', (ev) => {
    const el = ev.target as HTMLInputElement;
    const kind = el.dataset.glyph;
    if (!kind) return;
    const g = normalizeGlyph(el.value);
    if (g === null || g === SHIPPED_GLYPHS[kind]) delete live.cfg.glyphs[kind];
    else live.cfg.glyphs[kind] = g;
    const edited = kind in live.cfg.glyphs;
    el.classList.toggle('edited', edited);
    const reset = glyphs.querySelector<HTMLElement>(`[data-glyph-reset="${kind}"]`);
    if (reset) reset.hidden = !edited;
  });
  glyphs?.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('[data-glyph-reset]');
    if (!btn) return;
    const kind = btn.dataset.glyphReset!;
    delete live.cfg.glyphs[kind];
    const input = glyphs.querySelector<HTMLInputElement>(`[data-glyph="${kind}"]`);
    if (input) {
      input.value = SHIPPED_GLYPHS[kind] ?? '';
      input.classList.remove('edited');
    }
    btn.hidden = true;
  });

  host.querySelector('#mk-addkind')?.addEventListener('click', () => {
    const sel = host.querySelector('#mk-newkind') as HTMLSelectElement | null;
    const kind = sel?.value;
    if (!kind) return;
    live.cfg.kinds[kind] = {
      ...live.cfg.default,
      stages: live.cfg.default.stages.map((s) => ({ ...s })),
    };
    rerender(live.cfg);
  });

  host.querySelector('#mk-reset')?.addEventListener('click', () => {
    // Deliberately not a save: it loads the shipped values into the editor and
    // waits, so "reset" is still one undo (close the console) away from being
    // reversible until Save is pressed.
    rerender({
      default: { ...DEFAULT_MARKER_STYLE, stages: [] },
      kinds: {},
      glyphs: {},
    });
  });

  host.querySelector('#mk-save')?.addEventListener('click', () => {
    void onSave(live.cfg);
  });

  const frame = () => {
    const t = performance.now();
    for (const p of live.previews) {
      if (p.host.isConnected) {
        drawPreview(p.host, p.style(), glyphOf(p.kind, live.cfg), p.ageMs, t);
      }
    }
    live.raf = requestAnimationFrame(frame);
  };
  live.raf = requestAnimationFrame(frame);
  return () => cancelAnimationFrame(live.raf);
}
